import { useCallback, useEffect, useRef, useState } from "react";
import { useBlocker, useNavigate } from "react-router-dom";
import { getLastAppRoute } from "../../app/utils/lastAppRoute";

// These overlays live in Shopify's host. Their asynchronous close must finish
// before React Router removes the elements and their portal contents.
async function hideSettingsOverlays() {
    for (const id of ["storefront-unsaved-close-confirmation", "storefront-settings-editor"]) {
        const element = document.getElementById(id) as (HTMLElement & { hide?: () => Promise<void> }) | null;
        await element?.hide?.();
    }
}

export function useSettingsNavigation(hasUnsavedChanges: boolean, discard: () => void) {
    const navigate = useNavigate();
    const blocker = useBlocker(({ currentLocation, nextLocation }) => currentLocation.pathname !== nextLocation.pathname);
    const latestBlocker = useRef(blocker);
    latestBlocker.current = blocker;
    const closing = useRef(false);
    const [settingsEditorOpen, setSettingsEditorOpen] = useState(true);
    const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
    const [navigationError, setNavigationError] = useState<string>();

    useEffect(() => {
        if (!hasUnsavedChanges) return;
        const preventUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", preventUnload);
        return () => window.removeEventListener("beforeunload", preventUnload);
    }, [hasUnsavedChanges]);

    const finishExit = useCallback(async () => {
        if (closing.current) return;
        closing.current = true;
        setNavigationError(undefined);
        try {
            await hideSettingsOverlays();
            // A second Back/menu click can replace the pending destination while
            // Shopify is closing. Resume the latest request, never a stale one.
            const pending = latestBlocker.current;
            if (pending.state === "blocked") pending.proceed();
        } catch {
            closing.current = false;
            const pending = latestBlocker.current;
            if (pending.state === "blocked") pending.reset();
            setConfirmCloseOpen(false);
            setSettingsEditorOpen(true);
            setNavigationError("Settings could not close. Please try again.");
        }
    }, []);

    useEffect(() => {
        if (blocker.state !== "blocked" || closing.current) return;
        if (hasUnsavedChanges) {
            setSettingsEditorOpen(false);
            setConfirmCloseOpen(true);
        } else {
            void finishExit();
        }
    }, [blocker, finishExit, hasUnsavedChanges]);

    const handleSettingsEditorHide = useCallback(() => {
        if (closing.current || latestBlocker.current.state !== "unblocked") return;
        navigate(getLastAppRoute(), { replace: true });
    }, [navigate]);

    const stayInSettingsEditor = useCallback(() => {
        if (closing.current) return;
        const pending = latestBlocker.current;
        if (pending.state === "blocked") pending.reset();
        setConfirmCloseOpen(false);
        setSettingsEditorOpen(true);
    }, []);

    const leaveSettingsEditor = useCallback(() => {
        if (closing.current) return;
        discard();
        void finishExit();
    }, [discard, finishExit]);

    return {
        settingsEditorOpen, setSettingsEditorOpen, confirmCloseOpen, navigationError,
        handleSettingsEditorHide, stayInSettingsEditor, leaveSettingsEditor,
        handleConfirmCloseHide: stayInSettingsEditor,
    };
}
